/**
 * Region-based template assembly (#299, #300).
 *
 * The composition model these newsletters actually use: an operator designs a
 * whole HTML email — masthead, sponsor banner, section headings, footer — and
 * marks the places where feed stories go. Each firing expands those regions
 * from the current feed and leaves everything else untouched.
 *
 * This is the inverse of `buildEdition`, which constructs a body out of feed
 * items and discards whatever the operator composed. That is right for a bare
 * list of links and wrong for a designed publication, where the template IS the
 * product and the feed only supplies its contents.
 *
 * A region is delimited by HTML comments and repeated once per story:
 *
 *   <!-- START MAIN STORY -->   …{{HEADLINE}}…{{IMG}}…{{URL}}…   <!-- END MAIN STORY -->
 *
 * so the markup between them is a per-story template. Story 0 uses MAIN, the
 * rest use BODY — position, not category, picks the layout. Comment delimiters
 * rather than a custom syntax because they survive every WYSIWYG email editor:
 * an operator can open the template in their designer, move a region, and the
 * markers come back intact.
 */
/**
 * The per-edition identifier the ad server and analytics both key on (#301).
 *
 * This format is an EXTERNAL CONTRACT, not an internal choice. It appears as
 * `p=` on every ad impression and click URL, and as `utm_id` on every story
 * link, and neither the ad server nor the analytics reporting changes at
 * cutover — so it must be reproduced exactly.
 *
 * `base64(cleanedDisplayName + "YYYY-MM-DD HH:MM:SS")`, with a literal space
 * before the time. Two independent samples decoded from delivered mail:
 *
 *   VDNadtagsinSDDevDailyNewsnewvdntemplate2026-03-11 11:26:00
 *   LocalNewsFlash2023-06-02 14:16:00
 *
 * addressium's own edition `campaignId` is a cleaner identifier and is what we
 * use internally. Substituting it here would silently break continuity in
 * reporting nobody would notice until a monthly reconciliation — which is
 * exactly why this is the one place copying the legacy implementation is
 * correct rather than lazy.
 *
 * The name is stripped to `[A-Za-z0-9-]`; the TIMESTAMP keeps its spaces and
 * colons, which is why the result is not url-safe and callers must encode it.
 */
export function placementId(displayName, sendTime) {
    const cleaned = displayName.replace(/[^A-Za-z0-9-]/g, "");
    const d = typeof sendTime === "string" ? sendTime : formatSendTime(sendTime);
    // btoa is not available in every Node target; Buffer is, and both services
    // that need this run on Node.
    return Buffer.from(`${cleaned}${d}`, "utf8").toString("base64");
}
/** `YYYY-MM-DD HH:MM:SS` in UTC — the shape the legacy timestamps carry. */
export function formatSendTime(at) {
    const p = (n) => String(n).padStart(2, "0");
    return (`${at.getUTCFullYear()}-${p(at.getUTCMonth() + 1)}-${p(at.getUTCDate())} ` +
        `${p(at.getUTCHours())}:${p(at.getUTCMinutes())}:${p(at.getUTCSeconds())}`);
}
/** Per-story placeholders, substituted inside a repeated region. */
const STORY_TOKENS = ["URL", "HEADLINE", "IMG", "BODYTEXT"];
/** Escape a value being substituted into an HTML attribute or text node. */
function escapeHtml(s) {
    return s
        .replace(/&/g, "&amp;")
        .replace(/</g, "&lt;")
        .replace(/>/g, "&gt;")
        .replace(/"/g, "&quot;")
        .replace(/'/g, "&#39;");
}
/**
 * Find `<!-- START X -->…<!-- END X -->` (or BEGIN/END), tolerating the
 * whitespace variations real templates carry — `<!--- BEGIN X-->` appears in
 * the production template alongside the canonical spelling.
 */
export function findRegion(html, name) {
    const open = new RegExp(`<!--+\\s*(?:START|BEGIN)\\s+${name}\\s*-*->`, "i");
    const close = new RegExp(`<!--+\\s*END\\s+${name}\\s*-*->`, "i");
    const o = open.exec(html);
    if (!o)
        return undefined;
    const afterOpen = o.index + o[0].length;
    const c = close.exec(html.slice(afterOpen));
    if (!c)
        return undefined;
    return {
        inner: html.slice(afterOpen, afterOpen + c.index),
        start: o.index,
        end: afterOpen + c.index + c[0].length,
    };
}
/** Substitute one story's values into a region's per-story markup. */
export function renderStory(itemHtml, story) {
    let out = itemHtml;
    for (const token of STORY_TOKENS) {
        const value = token === "URL" ? story.url
            : token === "HEADLINE" ? story.headline
                : token === "IMG" ? (story.image ?? "")
                    : (story.bodyText ?? "");
        // Global, because {{URL}} appears several times in one story region — the
        // image link, the headline link and the Read More button all point at it.
        out = out.split(`{{${token}}}`).join(escapeHtml(value));
    }
    return out;
}
/**
 * Expand one region: repeat its markup per story, interleaving ads.
 *
 * Returns the replacement HTML for the whole delimited span. The caller splices
 * it back so the surrounding template is preserved byte for byte.
 */
export function expandRegion(region, stories, opts = {}) {
    const chosen = opts.maxItems === undefined ? stories : stories.slice(0, opts.maxItems);
    const ads = opts.ads ?? [];
    const parts = [];
    chosen.forEach((story, i) => {
        const markup = i === 0 && region.lead !== undefined ? region.lead : region.item;
        parts.push(renderStory(markup, story));
        // One ad after each story, in order, while any remain. No wrap-around and
        // no reuse: an ad is a sold placement, not a decoration to repeat.
        const ad = ads[i];
        if (ad !== undefined) {
            parts.push(opts.adWrapper ? opts.adWrapper.split("{{ADPLACEMENT}}").join(ad) : ad);
        }
    });
    return parts.join("\n");
}
//# sourceMappingURL=sections.js.map